// Meta Pixel utility — handles initialization and event tracking for LOS 12
// This SPA fires only: PageView, ViewContent, InitiateCheckout
// Purchase is handled by Shopify separately. Do not duplicate it here.

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void
      queue?: unknown[]
      push?: unknown
      loaded?: boolean
      version?: string
    }
    _fbq?: Window['fbq']
  }
}

const PIXEL_ID = '2061664838561549'

let initialized = false

/**
 * Loads the Meta Pixel script and fires the initial PageView.
 * Safe to call multiple times — only runs once.
 * Robust against environments where no <script> tag exists in the document.
 */
export function initMetaPixel(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (initialized || window.fbq) {
    initialized = true
    return
  }
  initialized = true

  try {
    // Set up fbq stub (queues calls until fbevents.js loads)
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const n: any = function () {
      // eslint-disable-next-line prefer-rest-params
      n.callMethod
        ? n.callMethod.apply(n, arguments as unknown as unknown[])
        : n.queue.push(arguments)
    }
    if (!window._fbq) window._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    window.fbq = n
    /* eslint-enable @typescript-eslint/no-explicit-any */

    // Append the pixel script directly to <head>, avoiding the
    // brittle "insertBefore first <script>" pattern.
    const scriptEl = document.createElement('script')
    scriptEl.async = true
    scriptEl.src = 'https://connect.facebook.net/en_US/fbevents.js'
    const target = document.head || document.body || document.documentElement
    if (target) {
      target.appendChild(scriptEl)
    }

    window.fbq && window.fbq('init', PIXEL_ID)
    window.fbq && window.fbq('track', 'PageView')

    // Noscript fallback pixel — appended only if body is available
    if (document.body) {
      const noscriptImg = document.createElement('img')
      noscriptImg.height = 1
      noscriptImg.width = 1
      noscriptImg.style.display = 'none'
      noscriptImg.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`
      noscriptImg.alt = ''
      document.body.appendChild(noscriptImg)
    }
  } catch (err) {
    // Never let pixel init crash the app
    // eslint-disable-next-line no-console
    console.warn('[metaPixel] init failed:', err)
  }
}

// Maps URL paths → sign labels for ViewContent
const zodiacPages: Record<string, string> = {
  '/aries': 'Aries',
  '/tauro': 'Tauro',
  '/geminis': 'Geminis',
  '/géminis': 'Geminis',
  '/cancer': 'Cancer',
  '/cáncer': 'Cancer',
  '/leo': 'Leo',
  '/virgo': 'Virgo',
  '/libra': 'Libra',
  '/escorpio': 'Escorpio',
  '/sagitario': 'Sagitario',
  '/capricornio': 'Capricornio',
  '/acuario': 'Acuario',
  '/piscis': 'Piscis',
}

// Track which paths have already fired ViewContent to prevent duplicates
const viewedPaths = new Set<string>()

/**
 * Fires a ViewContent event for the current path.
 * Deduplicates per path so the same page never fires ViewContent twice in one session.
 */
export function trackViewContent(pathname: string): void {
  if (typeof window === 'undefined' || !window.fbq) return

  const normalizedPath = pathname.toLowerCase()
  if (viewedPaths.has(normalizedPath)) return
  viewedPaths.add(normalizedPath)

  try {
    const currentSign = zodiacPages[normalizedPath]
    if (currentSign) {
      window.fbq('track', 'ViewContent', {
        content_name: currentSign,
        content_category: 'LOS12_Zodiac_Realm',
        content_type: 'product',
        value: 9.99,
        currency: 'USD',
      })
    } else {
      window.fbq('track', 'ViewContent', {
        content_name: 'LOS12_Home',
        content_category: 'LOS12_Zodiac_Selection',
        content_type: 'landing_page',
      })
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[metaPixel] ViewContent failed:', err)
  }
}

/**
 * Fires InitiateCheckout and redirects to the Shopify checkout URL.
 * @param sign - Sign name (e.g. 'Aries')
 * @param url - Shopify cart/checkout URL
 * @param value - Product value in USD (default 9.99 for the Núcleo)
 */
export function startCheckout(
  sign: string,
  url: string,
  value: number = 9.99,
): void {
  try {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: sign,
        content_category: 'LOS12_Zodiac_Realm',
        content_type: 'product',
        value: value,
        currency: 'USD',
      })
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[metaPixel] InitiateCheckout failed:', err)
  }
  setTimeout(() => {
    window.location.href = url
  }, 300)
}
